import React, { Fragment } from 'react';
import axios from 'axios';
import { Button, Form, Header, Icon, Loader, Responsive, Segment, Tab } from 'semantic-ui-react';
import { toast, ToastContainer } from 'react-toastify';
import { FormattedMessage } from 'react-intl';

import {
    fetchCantons,
    fetchContactTypes,
    fetchCountries,
    fetchEntities,
    fetchEntityTypes,
    fetchMunicipalities
} from '../Lookups/util';
import { FiltersOnStyle, IconHeader } from '../util/css';
import LeaseholderContact from './LeaseholderContactForm';
import LeaseholderAddress from './LeaseholderAddressForm';
import {
    ADMINISTRATOR,
    FACULTY,
    FACULTY_ADMIN,
    fetchActiveRole,
    fetchUserOrganizationalUnit,
    UNIVERSITY,
    UNIVERSITY_ADMIN,
    UNIVERSITY_SUPER_ADMIN
} from '../util/roles';
import LeaseholderDetails from './LeaseholderDetails';
import LeaseholderForm from './LeaseholderForm';
import PhysicalEntityTable from './PhysicalEntityTable';
import LegalEntityTable from './LegalEntityTable';
import PaginationSegment from '../util/PaginationSegment';
import { checkOrganizationalUnitsPrivilege, getTotalNumberOfPages } from '../util/util';
import { fetchFaculties } from '../../Lookups/util';
import ReactExport from 'react-data-export';

const { ExcelFile } = ReactExport;
const { ExcelSheet } = ReactExport.ExcelFile;

axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.withCredentials = true;

const DEFAULT_NUMBER_OF_ITEMS_PER_PAGE = 10;

const PHYSICAL_ENTITY_COLUMNS = [
    { key: 'name', displayName: 'Ime i prezime', accessor: (el) => el.physicalEntity.firstName + ' ' + el.physicalEntity.lastName },
    { key: 'comment', displayName: 'Napomena', accessor: (el) => el.comment || '-' },
];

const LEGAL_ENTITY_COLUMNS = [
    { key: 'name', displayName: 'Naziv', accessor: (el) => el.legalEntity.name },
    { key: 'idNumber', displayName: 'ID broj', accessor: (el) => el.legalEntity.idNumber || '-' },
    { key: 'comment', displayName: 'Napomena', accessor: (el) => el.comment || '-' },
];

const sanitize = (val) => {
    if (val == null) return '';
    if (val === '-') return '';
    if (typeof val === 'boolean') return val ? 'Da' : 'Ne';
    return String(val);
};

const PHYSICAL_SEARCH_FIELDS = ['_searchName', '_searchComment'];
const LEGAL_SEARCH_FIELDS = ['_searchName', '_searchIdNumber', '_searchComment'];

const addPhysicalSearchFields = (items) => items.map(item => ({
    ...item,
    _searchName: item.physicalEntity ? item.physicalEntity.firstName + ' ' + item.physicalEntity.lastName : '',
    _searchComment: item.comment ? item.comment : '',
}));

const addLegalSearchFields = (items) => items.map(item => ({
    ...item,
    _searchName: item.legalEntity && item.legalEntity.name ? item.legalEntity.name : '',
    _searchIdNumber: item.legalEntity && item.legalEntity.idNumber ? item.legalEntity.idNumber : '',
    _searchComment: item.comment ? item.comment : '',
}));

const preparePhysicalExcelData = (items) => [{
    columns: PHYSICAL_ENTITY_COLUMNS.map(col => ({
        title: col.displayName,
        width: { wpx: 200 }
    })),
    data: items.map(item =>
        PHYSICAL_ENTITY_COLUMNS.map(col => ({
            value: sanitize(col.accessor(item))
        }))
    )
}];

const prepareLegalExcelData = (items) => [{
    columns: LEGAL_ENTITY_COLUMNS.map(col => ({
        title: col.displayName,
        width: { wpx: 200 }
    })),
    data: items.map(item =>
        LEGAL_ENTITY_COLUMNS.map(col => ({
            value: sanitize(col.accessor(item))
        }))
    )
}];

class Leaseholder extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            contactTypes: [],
            municipalities: [],
            cantons: [],
            entities: [],
            countries: [],
            physicalLeaseholders: [],
            legalLeaseholders: [],
            organizationalUnitLeaseholders: [],
            allPhysicalLeaseholders: [],
            allLegalLeaseholders: [],
            filteredPhysicalLeaseholders: [],
            filteredLegalLeaseholders: [],
            filteredOrganizationalUnitLeaseholders: [],
            physicalSearchText: '',
            legalSearchText: '',
            physicalExcelData: [],
            legalExcelData: [],
            organizationalUnits: [],
            entityTypes: [],
            lookups: {
                entityTypes: [],
            },
            selectedOrganizationalUnit: null,
            requestMode: null,
            showFilterMode: false,
            contactEditMode: false,
            addressEditMode: false,
            leaseholderData: {
                contacts: [],
                addresses: [],
            },
            details: false,
            leaseholderObject: null,

            // For pagination
            activePage: 1,
            totalPages: 1,
            resultsPerPage: DEFAULT_NUMBER_OF_ITEMS_PER_PAGE,

            physicalActivePage: 1,
            physicalTotalPages: 1,
            physicalResultsPerPage: DEFAULT_NUMBER_OF_ITEMS_PER_PAGE,

            legalActivePage: 1,
            legalTotalPages: 1,
            legalResultsPerPage: DEFAULT_NUMBER_OF_ITEMS_PER_PAGE,

            organizationalUnitActivePage: 1,
            organizationalUnitTotalPages: 1,
            organizationalUnitResultsPerPage: DEFAULT_NUMBER_OF_ITEMS_PER_PAGE,

            filterRequestData: null,
            loader: true,

            activeIndex: 0,

            deletingLeaseholderRowId: null,
            deletingLeaseholderSaving: false,
        };

        this.fetchOrgUnits = this.fetchOrgUnits.bind(this);
        this.activateEditRequest = this.activateEditRequest.bind(this);
        this.activateAddRequest = this.activateAddRequest.bind(this);
        this.getLeaseholders = this.getLeaseholders.bind(this);
        this.getDataToRender = this.getDataToRender.bind(this);
        this.switchContactEditMode = this.switchContactEditMode.bind(this);
        this.switchAddressEditMode = this.switchAddressEditMode.bind(this);
        this.onAddNewAddress = this.onAddNewAddress.bind(this);
        this.onAddNewContact = this.onAddNewContact.bind(this);
        this.switchToDetails = this.switchToDetails.bind(this);
        this.switchToTable = this.switchToTable.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
        this.handlePhysicalPageChange = this.handlePhysicalPageChange.bind(this);
        this.handleLegalPageChange = this.handleLegalPageChange.bind(this);
        this.handleResultsPerPageChange = this.handleResultsPerPageChange.bind(this);
        this.activateContactRequest = this.activateContactRequest.bind(this);
        this.activateAddressRequest = this.activateAddressRequest.bind(this);
        this.onChangeOrganizationalUnit = this.onChangeOrganizationalUnit.bind(this);
        this.onDeleteContact = this.onDeleteContact.bind(this);
        this.onDeleteAddress = this.onDeleteAddress.bind(this);
    }

    componentDidMount() {
        this.getUserData();
    }

    fetchLeaseholders(page = 0, resultsPerPage = DEFAULT_NUMBER_OF_ITEMS_PER_PAGE) {
        const pagination = {
            page,
            resultsPerPage,
        };
        return axios.post('/assets/leaseholders/paginated/', { ...pagination });
    }

    fetchAllLeaseholders(page = 0, resultsPerPage = DEFAULT_NUMBER_OF_ITEMS_PER_PAGE) {
        const pagination = {
            page,
            resultsPerPage,
        };
        return axios.post('/assets/leaseholders/all/', { ...pagination });
    }

    getUserData() {
        axios.all([fetchActiveRole(), fetchUserOrganizationalUnit()])
            .then(axios.spread((activeRoleRes, userOrganizationalUnitRes) => {
                const activeRole = activeRoleRes.data;
                const organizationalUnitId = userOrganizationalUnitRes.data;

                this.setState({ activeRole, organizationalUnitId });
                this.fetchLookups();
            }))
            .catch(err => {
                this.setState({ error: true, loader: false });
                // console.log('Failed to fetch data.');
            });
    }

    getLeaseholders(page, resultsPerPage) {
        const { activeRole } = this.state;
        if (activeRole === ADMINISTRATOR || activeRole === UNIVERSITY || activeRole === UNIVERSITY_ADMIN || activeRole === UNIVERSITY_SUPER_ADMIN) {
            this.fetchAllLeaseholders(page, resultsPerPage)
                .then(leaseholderRes => {
                    const physicalLeaseholders = leaseholderRes.data.responseList.filter(el => el.entityType.id === 1);
                    const legalLeaseholders = leaseholderRes.data.responseList.filter(el => el.entityType.id === 2);
                    const organizationalUnitLeaseholders = leaseholderRes.data.responseList.filter(el => el.entityType.id === 3);
                    let totalPages = parseInt(leaseholderRes.data.total / resultsPerPage, 10);
                    totalPages += leaseholderRes.data.total % resultsPerPage !== 0 ? 1 : 0;
                    this.setState({
                        physicalLeaseholders,
                        legalLeaseholders,
                        organizationalUnitLeaseholders,
                        totalPages,
                        activePage: page + 1,
                        resultsPerPage,
                        loader:false
                    });
                })
                .catch(err => {
                    toast('Došlo je do greške!');
                    // console.log('Failed to fetch data.');
                });
        } else if (activeRole === FACULTY_ADMIN || activeRole === FACULTY || activeRole === UNIVERSITY_SUPER_ADMIN) {
            this.fetchLeaseholders(page, resultsPerPage)
                .then(leaseholderRes => {
                    const physicalLeaseholders = leaseholderRes.data.responseList.filter(el => el.entityType.id === 1);
                    const legalLeaseholders = leaseholderRes.data.responseList.filter(el => el.entityType.id === 2);
                    const organizationalUnitLeaseholders = leaseholderRes.data.responseList.filter(el => el.entityType.id === 3);
                    let totalPages = parseInt(leaseholderRes.data.total / resultsPerPage, 10);
                    totalPages += leaseholderRes.data.total % resultsPerPage !== 0 ? 1 : 0;
                    this.setState({
                        physicalLeaseholders,
                        legalLeaseholders,
                        organizationalUnitLeaseholders,
                        totalPages,
                        activePage: page + 1,
                        resultsPerPage,
                        loader: false
                    });
                })
                .catch(err => {
                    toast('Došlo je do greške!');
                    // console.log('Failed to fetch data.');
                });
        }
    }

    fetchLookups() {
        this.setState({ loader: true });
        axios.all([fetchFaculties(), fetchEntityTypes(), fetchMunicipalities(), fetchCantons(), fetchEntities(), fetchCountries(), fetchContactTypes()])
            .then(axios.spread((organizationalUnitsRes, entityTypesRes, municipalitiesRes, cantonsRes, entitiesRes, countriesRes, contactTypesRes) => {
                const organizationalUnits = organizationalUnitsRes.data.map((el) => ({
                    ...el, key: el.id, value: el.id, text: el.name
                }));
                const municipalities = municipalitiesRes.data.map(el => ({
                    ...el, key: el.id, value: el.id, text: el.name,
                }));
                const cantons = cantonsRes.data.map(el => ({
                    ...el, key: el.id, value: el.id, text: el.name,
                }));
                const entities = entitiesRes.data.map(el => ({
                    ...el, key: el.id, value: el.id, text: el.name,
                }));
                const countries = countriesRes.data.map(el => ({
                    ...el, key: el.id, value: el.id, text: el.name,
                }));
                const entityTypes = entityTypesRes.data.map(el => ({
                    ...el, key: el.id, value: el.id, text: el.name,
                }));
                const contactTypes = contactTypesRes.data.map(el => ({
                    ...el, key: el.id, value: el.id, text: el.name,
                }));
                const lookups = {
                    entityTypes,
                };
                this.setState({
                    organizationalUnits,
                    municipalities,
                    cantons,
                    entities,
                    countries,
                    entityTypes,
                    contactTypes,
                    lookups,
                });
                if (!this.getOrganizationalUnitsPrivilege()) {
                    Promise.all([
                        this.fetchLeaseholdersByType(3),
                        this.fetchLeaseholdersByType(2),
                        this.fetchLeaseholdersByType(1),
                    ]).then(([orgUnitRes, legalRes, physicalRes]) => {
                        const organizationalUnitLeaseholders = orgUnitRes.data.responseList;
                        let organizationalUnitTotalPages = getTotalNumberOfPages(orgUnitRes.data.total, DEFAULT_NUMBER_OF_ITEMS_PER_PAGE);
                        const legalLeaseholders = legalRes.data.responseList;
                        const legalWithSearchFields = addLegalSearchFields(legalLeaseholders);
                        let legalTotalPages = getTotalNumberOfPages(legalRes.data.total, DEFAULT_NUMBER_OF_ITEMS_PER_PAGE);
                        const physicalLeaseholders = physicalRes.data.responseList.filter(el => el.entityType.id === 1);
                        const physicalWithSearchFields = addPhysicalSearchFields(physicalLeaseholders);
                        let physicalTotalPages = getTotalNumberOfPages(physicalRes.data.total, DEFAULT_NUMBER_OF_ITEMS_PER_PAGE);
                        this.setState({
                            organizationalUnitLeaseholders,
                            organizationalUnitTotalPages,
                            organizationalUnitActivePage: 1,
                            organizationalUnitResultsPerPage: DEFAULT_NUMBER_OF_ITEMS_PER_PAGE,
                            legalLeaseholders,
                            allLegalLeaseholders: legalWithSearchFields,
                            filteredLegalLeaseholders: legalWithSearchFields,
                            legalExcelData: prepareLegalExcelData(legalWithSearchFields),
                            legalActivePage: 1,
                            legalTotalPages,
                            legalResultsPerPage: DEFAULT_NUMBER_OF_ITEMS_PER_PAGE,
                            physicalLeaseholders,
                            allPhysicalLeaseholders: physicalWithSearchFields,
                            filteredPhysicalLeaseholders: physicalWithSearchFields,
                            physicalExcelData: preparePhysicalExcelData(physicalWithSearchFields),
                            physicalActivePage: 1,
                            physicalTotalPages,
                            physicalResultsPerPage: DEFAULT_NUMBER_OF_ITEMS_PER_PAGE,
                            loader: false,
                        });
                    }).catch(err => {
                        toast('Došlo je do greške!');
                        this.setState({ loader: false });
                    });
                } else {
                    this.setState({ loader: false });
                }
            }))
            .catch(err => {
                this.setState({ error: true, loader: false });
                // console.log('Failed to fetch data.');
            });
    }

    getWritePrivilege() {
        const activeRole = localStorage.getItem('activeRole');
        return activeRole.startsWith(FACULTY_ADMIN) || activeRole === UNIVERSITY_SUPER_ADMIN || activeRole === ADMINISTRATOR;
    }

    getOrganizationalUnitsPrivilege() {
        const activeRole = localStorage.getItem('activeRole');
        return activeRole === UNIVERSITY_SUPER_ADMIN || activeRole === UNIVERSITY_ADMIN || activeRole === ADMINISTRATOR;
    }

    fetchLeaseholdersByType(entityType, page = 0, resultsPerPage = DEFAULT_NUMBER_OF_ITEMS_PER_PAGE) {
        let filterRequestData = {
            organizationalUnit: this.getOrganizationalUnitsPrivilege()
                ? this.state.selectedOrganizationalUnit
                : this.state.organizationalUnitId,
            entityType,
            physicalEntity: null,
            legalEntity: null,
        };
        const paginationDTO = { page, resultsPerPage, order: null, fieldName: null };
        filterRequestData = { ...filterRequestData, paginationDTO };
        return axios.post('/assets/leaseholders/filter/', { ...filterRequestData });
    }

    onChangeOrganizationalUnit(selectedOrganizationalUnit) {
        this.setState({ selectedOrganizationalUnit }, () => {
            this.setState({ loader: true });
            Promise.all([
                this.fetchLeaseholdersByType(3, 0, this.state.organizationalUnitResultsPerPage),
                this.fetchLeaseholdersByType(2, 0, this.state.legalResultsPerPage),
                this.fetchLeaseholdersByType(1, 0, this.state.physicalResultsPerPage),
            ]).then(([orgUnitRes, legalRes, physicalRes]) => {
                const organizationalUnitLeaseholders = orgUnitRes.data.responseList;
                let organizationalUnitTotalPages = getTotalNumberOfPages(orgUnitRes.data.total, this.state.organizationalUnitResultsPerPage);
                const legalLeaseholders = legalRes.data.responseList;
                const legalWithSearchFields = addLegalSearchFields(legalLeaseholders);
                let legalTotalPages = getTotalNumberOfPages(legalRes.data.total, this.state.legalResultsPerPage);
                const physicalLeaseholders = physicalRes.data.responseList.filter(el => el.entityType.id === 1);
                const physicalWithSearchFields = addPhysicalSearchFields(physicalLeaseholders);
                let physicalTotalPages = getTotalNumberOfPages(physicalRes.data.total, this.state.physicalResultsPerPage);
                this.setState({
                    organizationalUnitLeaseholders,
                    organizationalUnitTotalPages,
                    organizationalUnitActivePage: 1,
                    legalLeaseholders,
                    allLegalLeaseholders: legalWithSearchFields,
                    filteredLegalLeaseholders: legalWithSearchFields,
                    legalExcelData: prepareLegalExcelData(legalWithSearchFields),
                    legalActivePage: 1,
                    legalTotalPages,
                    physicalLeaseholders,
                    allPhysicalLeaseholders: physicalWithSearchFields,
                    filteredPhysicalLeaseholders: physicalWithSearchFields,
                    physicalExcelData: preparePhysicalExcelData(physicalWithSearchFields),
                    physicalActivePage: 1,
                    physicalTotalPages,
                    loader: false,
                });
            }).catch(err => {
                toast('Došlo je do greške!');
                this.setState({ loader: false });
            });
        });
    }

    getPhysicalLeaseholders(page = 0, resultsPerPage = DEFAULT_NUMBER_OF_ITEMS_PER_PAGE) {
        if (this.getOrganizationalUnitsPrivilege() && !this.state.selectedOrganizationalUnit) return;
        this.fetchLeaseholdersByType(1, page, resultsPerPage)
            .then(res => {
                const physicalLeaseholders = res.data.responseList.filter(el => el.entityType.id === 1);
                const withSearchFields = addPhysicalSearchFields(physicalLeaseholders);
                let physicalTotalPages = getTotalNumberOfPages(res.data.total, resultsPerPage);
                this.setState({
                    physicalLeaseholders,
                    allPhysicalLeaseholders: withSearchFields,
                    filteredPhysicalLeaseholders: withSearchFields,
                    physicalExcelData: preparePhysicalExcelData(withSearchFields),
                    physicalActivePage: page + 1,
                    physicalTotalPages,
                    physicalResultsPerPage: resultsPerPage,
                });
            })
            .catch(err => {
                toast('Došlo je do greške!');
                // console.log('Error: Failed to fetch filter data\n', err);
            });
    }

    getLegalLeaseholders(page = 0, resultsPerPage = DEFAULT_NUMBER_OF_ITEMS_PER_PAGE) {
        if (this.getOrganizationalUnitsPrivilege() && !this.state.selectedOrganizationalUnit) return;
        this.fetchLeaseholdersByType(2, page, resultsPerPage)
            .then(res => {
                const legalLeaseholders = res.data.responseList;
                const withSearchFields = addLegalSearchFields(legalLeaseholders);
                let legalTotalPages = getTotalNumberOfPages(res.data.total, resultsPerPage);
                this.setState({
                    legalLeaseholders,
                    allLegalLeaseholders: withSearchFields,
                    filteredLegalLeaseholders: withSearchFields,
                    legalExcelData: prepareLegalExcelData(withSearchFields),
                    legalActivePage: page + 1,
                    legalTotalPages,
                    legalResultsPerPage: resultsPerPage,
                });
            })
            .catch(err => {
                toast('Došlo je do greške!');
                // console.log('Error: Failed to fetch filter data\n', err);
            });
    }

    getPhysicalResults = (results) => {
        if (results.length === 0 && this.state.physicalSearchText.trim() === '') {
            const withSearchFields = addPhysicalSearchFields(this.state.physicalLeaseholders);
            this.setState({
                filteredPhysicalLeaseholders: withSearchFields,
                physicalExcelData: preparePhysicalExcelData(withSearchFields),
            });
        } else {
            this.setState({
                filteredPhysicalLeaseholders: results,
                physicalExcelData: preparePhysicalExcelData(results),
            });
        }
    };

    getLegalResults = (results) => {
        if (results.length === 0 && this.state.legalSearchText.trim() === '') {
            const withSearchFields = addLegalSearchFields(this.state.legalLeaseholders);
            this.setState({
                filteredLegalLeaseholders: withSearchFields,
                legalExcelData: prepareLegalExcelData(withSearchFields),
            });
        } else {
            this.setState({
                filteredLegalLeaseholders: results,
                legalExcelData: prepareLegalExcelData(results),
            });
        }
    };

    handlePhysicalSearchTextChange = (text) => {
        this.setState({ physicalSearchText: text });
    };

    handleLegalSearchTextChange = (text) => {
        this.setState({ legalSearchText: text });
    };

    getDataToRender() {
        return {
            physicalLeaseholders: this.state.filteredPhysicalLeaseholders,
            legalLeaseholders: this.state.filteredLegalLeaseholders,
            organizationalUnitLeaseholders: this.state.organizationalUnitLeaseholders,
        };
    }

    fetchOrgUnits() {
        axios.all([fetchFaculties(), fetchEntityTypes()])
            .then(axios.spread((organizationalUnitsRes, entityTypesRes) => {
                const organizationalUnits = organizationalUnitsRes.data.map(el => ({
                    ...el, key: el.id, value: el.id, text: el.name,
                }));
                const entityTypes = entityTypesRes.data.map(el => ({
                    ...el, key: el.id, value: el.id, text: el.name,
                }));
                const lookups = {
                    entityTypes,
                };
                this.setState({
                    organizationalUnits,
                    entityTypes,
                    lookups,
                    loader: false,
                });
            }))
            .catch(err => {
                this.setState({ error: true, loader: false });
                // console.log('Failed to fetch data.');
            });
    }

    activateEditRequest(el) {
        this.setState({ leaseholderObject: el, requestMode: 'edit' });
    }

    activateContactRequest(el) {
        this.setState({ leaseholderObject: el, requestMode: 'contact' });
    }

    activateAddressRequest(el) {
        this.setState({ leaseholderObject: el, requestMode: 'address' });
    }

    activateAddRequest() {
        this.setState({ leaseholderObject: null, requestMode: 'add' });
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    switchContactEditMode() {
        this.setState(prevState => ({ contactEditMode: !prevState.contactEditMode }));
    }

    onAddNewContact(contact) {
        const contactRequest = {
            leaseholder: { id: this.state.leaseholderObject.id },
            contactType: contact.contactType,
            comment: contact.comment,
            value: contact.value,
        };
        return axios.post('/assets/leaseholder-contacts/', { ...contactRequest })
            .then((res) => {
                // console.log('Uspjesno: ', res.data);
                axios.all([this.fetchLeaseholderContacts(this.state.leaseholderObject.id)])
                    .then(axios.spread((leaseholderContactsRes) => {
                        const leaseholderData = {
                            addresses: this.state.leaseholderData.addresses,
                            contacts: leaseholderContactsRes.data,
                        };
                        this.setState({
                            leaseholderData,
                        });
                        toast('Uspješno dodan kontakt!');
                    }))
                    .catch(err => {
                        // console.log('Error: ', err);
                    });
            })
            .catch((err) => {
                // console.log('Error: ', err);
                throw err;
            });
    }

    switchAddressEditMode() {
        this.setState(prevState => ({ addressEditMode: !prevState.addressEditMode }));
    }

    onAddNewAddress(address) {
        const place = {
            name: address.place,
            municipality: address.municipality,
            canton: address.canton,
            entity: address.entity,
            country: address.country,
        };
        const addressRequest = {
            leaseholder: { id: this.state.leaseholderObject.id },
            place,
            street: address.street,
        };
        // console.log(addressRequest);
        return axios.post('/assets/leaseholder-addresses/', { ...addressRequest })
            .then((res) => {
                // console.log('Uspjesno: ', res.data);
                axios.all([this.fetchLeaseholderAddresses(this.state.leaseholderObject.id)])
                    .then(axios.spread((leaseholderAddressesRes) => {
                        const leaseholderData = {
                            addresses: leaseholderAddressesRes.data,
                            contacts: this.state.leaseholderData.contacts,
                        };
                        this.setState({
                            leaseholderData,
                        });
                    }))
                    .catch(err => {
                        // console.log('Error: ', err);
                    });
                toast('Uspješno dodana adresa!');
            })
            .catch((err) => {
                // console.log('Error: ', err);
                throw err;
            });
    }

    onDeleteAddress(addressId) {
        return axios.delete('/assets/leaseholder-addresses/', { data: { id: addressId } })
            .then(() => {
                // Update state locally instead of refetching from server
                this.setState(prevState => {
                    const prevAddresses = (prevState.leaseholderData && prevState.leaseholderData.addresses) || [];
                    const newAddresses = prevAddresses.filter(a => a.id !== addressId);
                    return {
                        leaseholderData: {
                            addresses: newAddresses,
                            contacts: prevState.leaseholderData ? prevState.leaseholderData.contacts : [],
                        },
                    };
                });
                toast('Uspješno brisanje adrese!');
            })
            .catch(err => {
                const msg = err && err.response && err.response.data ? err.response.data : '';
                if (msg.includes('ConstraintViolationException')) {
                    toast(<FormattedMessage id="assets.leaseholders.addressDeleteConstraintError"/>);
                } else {
                    toast(<FormattedMessage id="assets.leaseholders.addressDeleteError"/>);
                }
                throw err;
            });
    }

    onDeleteContact(contactId) {
        return axios.delete(`/assets/leaseholder-contacts/${contactId}/`)
            .then(() => {
                // Update state locally instead of refetching from server
                this.setState(prevState => {
                    const prevContacts = (prevState.leaseholderData && prevState.leaseholderData.contacts) || [];
                    const newContacts = prevContacts.filter(c => c.id !== contactId);
                    return {
                        leaseholderData: {
                            addresses: prevState.leaseholderData ? prevState.leaseholderData.addresses : [],
                            contacts: newContacts,
                        },
                    };
                });
                toast('Uspješno brisanje kontakta!');
            })
            .catch(err => {
                const msg = err && err.response && err.response.data ? err.response.data : '';
                if (msg.includes('ConstraintViolationException')) {
                    toast(<FormattedMessage id="assets.leaseholders.contactDeleteConstraintError"/>);
                } else {
                    toast(<FormattedMessage id="assets.leaseholders.contactDeleteError"/>);
                }
                throw err;
            });
    }

    handleLeaseholderDeleteClick = (id) => {
        this.setState({ deletingLeaseholderRowId: id });
    }

    handleLeaseholderDeleteCancel = () => {
        this.setState({ deletingLeaseholderRowId: null });
    }

    handleLeaseholderDeleteConfirm = () => {
        const { deletingLeaseholderRowId } = this.state;
        this.setState({ deletingLeaseholderSaving: true });
        axios.delete(`/assets/leaseholders/${deletingLeaseholderRowId}/`)
            .then(() => {
                toast('Uspješno brisanje zakupca!');
                this.setState({ deletingLeaseholderRowId: null, deletingLeaseholderSaving: false });
                if (this.state.activeIndex === 0) {
                    this.getPhysicalLeaseholders(this.state.physicalActivePage - 1, this.state.physicalResultsPerPage);
                } else {
                    this.getLegalLeaseholders(this.state.legalActivePage - 1, this.state.legalResultsPerPage);
                }
            })
            .catch(err => {
                const msg = err && err.response && err.response.data ? err.response.data : '';
                if (msg.includes('ConstraintViolationException')) {
                    toast(<FormattedMessage id="assets.leaseholders.deleteConstraintError"/>);
                } else {
                    toast(<FormattedMessage id="assets.leaseholders.deleteError"/>);
                }
                this.setState({ deletingLeaseholderRowId: null, deletingLeaseholderSaving: false });
            });
    }

    fetchLeaseholderAddresses(entityId) {
        return axios.get(`/assets/leaseholder-addresses/by-leaseholder/${entityId}/`);
    }

    fetchLeaseholderContacts(entityId) {
        return axios.get(`/assets/leaseholder-contacts/by-leaseholder/${entityId}/`);
    }

    switchToDetails(leaseholder) {
        return;
        // console.log(leaseholder);
        if (leaseholder !== null) {
            axios.all([this.fetchLeaseholderAddresses(leaseholder.id), this.fetchLeaseholderContacts(leaseholder.id)])
                .then(axios.spread((leaseholderAddressesRes, leaseholderContactsRes) => {
                    const leaseholderData = {
                        addresses: leaseholderAddressesRes.data,
                        contacts: leaseholderContactsRes.data,
                    };
                    this.setState({
                        leaseholderData,
                        details: true,
                        leaseholderObject: leaseholder,
                    });
                }))
                .catch(err => {
                    toast('Greška prilikom dobivanja adrese i kontakta zakupca!');
                    // console.log('Error: ', err);
                });
        }
    }

    switchToTable() {
        this.setState({
            leaseholderObject: null,
            details: false,
        });
    }

    handlePageChange(e, { activePage }) {
        if (activePage === this.state.activePage) return;
        this.getLeaseholders(activePage - 1, this.state.resultsPerPage);
    }

    handlePhysicalPageChange(e, { activePage }) {
        if (activePage === this.state.physicalActivePage) {
            return;
        }
        this.getPhysicalLeaseholders(activePage - 1, this.state.resultsPerPage);
    }

    handleLegalPageChange(e, { activePage }) {
        if (activePage === this.state.legalActivePage) {
            return;
        }
        this.getLegalLeaseholders(activePage - 1, this.state.resultsPerPage);
    }

    handleResultsPerPageChange(e, { value }) {
        this.getLeaseholders(0, value);
    }

    handleTabChange = (e, { activeIndex }) => this.setState({ activeIndex, requestMode: null });

    render() {
        const panes = [
            {
                menuItem: 'Fizička lica',
                render: () => (
                    <Tab.Pane attached={false} as={Responsive}>
                        <PhysicalEntityTable
                            allPhysicalLeaseholders={allPhysicalLeaseholders}
                            filteredPhysicalLeaseholders={filteredPhysicalLeaseholders}
                            writePrivilege={writePrivilege}
                            disabled={isFormActive}
                            deletingRowId={this.state.deletingLeaseholderRowId}
                            deletingSaving={this.state.deletingLeaseholderSaving}
                            onDelete={this.handleLeaseholderDeleteClick}
                            onDeleteConfirm={this.handleLeaseholderDeleteConfirm}
                            onDeleteCancel={this.handleLeaseholderDeleteCancel}
                            onAddress={this.activateAddressRequest}
                            onContact={this.activateContactRequest}
                            onEdit={this.activateEditRequest}
                            onDetails={this.switchToDetails}
                            physicalExcelData={physicalExcelData}
                            getPhysicalResults={this.getPhysicalResults}
                            handlePhysicalSearchTextChange={this.handlePhysicalSearchTextChange}
                            physicalSearchFields={PHYSICAL_SEARCH_FIELDS}
                            selectedOrganizationalUnit={this.state.selectedOrganizationalUnit}
                            activeRole={this.state.activeRole}
                        />
                    </Tab.Pane>
                )
            },
            {
                menuItem: 'Pravna lica',
                render: () => (
                    <Tab.Pane attached={false} as={Responsive}>
                        <LegalEntityTable
                            allLegalLeaseholders={allLegalLeaseholders}
                            filteredLegalLeaseholders={filteredLegalLeaseholders}
                            writePrivilege={writePrivilege}
                            disabled={isFormActive}
                            deletingRowId={this.state.deletingLeaseholderRowId}
                            deletingSaving={this.state.deletingLeaseholderSaving}
                            onDelete={this.handleLeaseholderDeleteClick}
                            onDeleteConfirm={this.handleLeaseholderDeleteConfirm}
                            onDeleteCancel={this.handleLeaseholderDeleteCancel}
                            onAddress={this.activateAddressRequest}
                            onContact={this.activateContactRequest}
                            onEdit={this.activateEditRequest}
                            onDetails={this.switchToDetails}
                            legalExcelData={legalExcelData}
                            getLegalResults={this.getLegalResults}
                            handleLegalSearchTextChange={this.handleLegalSearchTextChange}
                            legalSearchFields={LEGAL_SEARCH_FIELDS}
                            selectedOrganizationalUnit={this.state.selectedOrganizationalUnit}
                            activeRole={this.state.activeRole}
                        />
                    </Tab.Pane>
                )
            },
        ];
        const organizationalUnitPrivilege = this.getOrganizationalUnitsPrivilege();
        const writePrivilege = this.getWritePrivilege();
        const {
            leaseholderObject,
            details,
            leaseholderData
        } = this.state;
        const data = this.getDataToRender();
        const { physicalLeaseholders, legalLeaseholders, organizationalUnitLeaseholders } = data;
        if (details) {
            return (<LeaseholderDetails
                leaseholder={leaseholderObject}
                addresses={leaseholderData.addresses}
                contacts={leaseholderData.contacts}
                switchToTable={this.switchToTable}
            />);
        }
        const {
            allPhysicalLeaseholders,
            allLegalLeaseholders,
            filteredPhysicalLeaseholders,
            filteredLegalLeaseholders,
            physicalExcelData,
            legalExcelData,
            physicalSearchText,
            legalSearchText,
        } = this.state;
        const isFormActive = this.state.requestMode === 'edit' || this.state.requestMode === 'add';
        return (
            <div>
                <Header as="h1">Zakupci</Header>
                <Segment.Group raised>
                    <Segment>
                        <Header as="h3">
                            Spisak zakupaca

                            <Header style={IconHeader} floated="right" >
                                {writePrivilege &&
                                    <Button
                                        floated="left"
                                        icon
                                        labelPosition="left"
                                        primary
                                        size="small"
                                        style={FiltersOnStyle}
                                        disabled={isFormActive || !!this.state.deletingLeaseholderRowId}
                                        onClick={this.activateAddRequest}
                                    >
                                        <Icon className="add" />
                                        Dodavanje novog zakupca
                                    </Button>

                                }
                            </Header>
                        </Header>
                    </Segment>

                    {isFormActive &&
                    <LeaseholderForm
                        leaseholder={this.state.leaseholderObject}
                        activeRole={this.state.activeRole}
                        entityTypes={this.state.entityTypes}
                        organizationalUnits={this.state.organizationalUnits}
                        onRefreshData={() => {
                            this.getPhysicalLeaseholders(this.state.physicalActivePage - 1, this.state.physicalResultsPerPage);
                            this.getLegalLeaseholders(this.state.legalActivePage - 1, this.state.legalResultsPerPage);
                        }}
                        onCloseForm={() => { this.setState({ requestMode: null, leaseholderObject: null }); }}
                    />}
                    {organizationalUnitPrivilege &&
                        <Segment>
                            <Form>
                                <Form.Field>
                                    <label>Članica</label>
                                    <Form.Dropdown
                                        disabled={this.state.loader}
                                        search
                                        options={this.state.organizationalUnits}
                                        value={this.state.selectedOrganizationalUnit || null}
                                        selection
                                        clearable={false}
                                        placeholder={'Odaberite članicu'}
                                        onChange={(e, { value }) => this.onChangeOrganizationalUnit(value)}
                                    />
                                </Form.Field>
                            </Form>
                        </Segment>
                    }
                    {this.state.requestMode === 'contact' &&
                        <LeaseholderContact
                            contacts={this.state.leaseholderData.contacts || []}
                            editMode={this.state.contactEditMode}
                            contactTypes={this.state.contactTypes}
                            onAddNewContact={this.onAddNewContact}
                            onDeleteContact={this.onDeleteContact}
                            onEditContact={this.onEditContact}
                            onClose={() => this.setState({ requestMode: null })}
                            switchEditMode={this.switchContactEditMode}
                        />
                    }

                    {this.state.requestMode === 'address' &&
                        <LeaseholderAddress
                            addresses={this.state.leaseholderData.addresses || []}
                            editMode={this.state.addressEditMode}
                            municipalities={this.state.municipalities}
                            cantons={this.state.cantons}
                            entities={this.state.entities}
                            countries={this.state.countries}
                            onAddNewAddress={this.onAddNewAddress}
                            onDeleteAddress={this.onDeleteAddress}
                            onEditAddress={this.onEditAddress}
                            onClose={() => this.setState({ requestMode: null })}
                            switchEditMode={this.switchAddressEditMode}
                        />
                    }

                    <Segment>
                        {this.state.loader ?
                            <Loader active inline="centered"/>
                        :
                            <Fragment>
                                <Tab menu={{ secondary: true, pointing: true, color: 'blue' }} panes={panes} onTabChange={this.handleTabChange} />

                                {this.state.activeIndex === 0 && filteredPhysicalLeaseholders.length > 0 &&
                                    <PaginationSegment
                                        activePage={this.state.physicalActivePage}
                                        totalPages={this.state.physicalTotalPages}
                                        onPageChange={this.handlePhysicalPageChange}
                                    />
                                }
                                {this.state.activeIndex === 1 && filteredLegalLeaseholders.length > 0 &&
                                    <PaginationSegment
                                        activePage={this.state.legalActivePage}
                                        totalPages={this.state.legalTotalPages}
                                        onPageChange={this.handleLegalPageChange}
                                    />
                                }
                            </Fragment>
                        }
                    </Segment>
                    <ToastContainer hideProgressBar />
                </Segment.Group>
            </div>
        );
    }
}
export default Leaseholder;



// WEBPACK FOOTER //
// ./src/views/Assets/Leaseholder/Leaseholder.js